'use client';

import { useState } from 'react';
import Link from 'next/link';
import useSWR from 'swr';
import { swrFetcher } from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { useEshopCart } from '../../lib/useEshopCart';
import { useToast } from '../../components/Toast';
import Alert from '../../components/Alert';
import LoadingFallback from '../../components/LoadingFallback';

export const dynamic = 'force-dynamic';

type Product = {
  id: number;
  name: string;
  slug: string;
  shortDescription: string | null;
  sku: string | null;
  price: number | null;
  groupPrice: number | null;
  priceGroupName: string | null;
  currency: string;
  stockQuantity: number | null;
  imageAssetId: number | null;
  isFeatured: boolean;
  categoryId: number | null;
  categorySlug: string | null;
  categoryName: string | null;
};

type Category = {
  id: number;
  parentId: number | null;
  name: string;
  slug: string;
  productCount: number;
};

type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
};

const SORTS = ['default', 'name', 'priceAsc', 'priceDesc', 'newest'] as const;

type HomepageSection = {
  type?: string;
  title?: string;
  body?: string;
  imageAssetId?: number | null;
  linkUrl?: string;
  linkLabel?: string;
};

export default function ShopPage() {
  const { t } = useTranslation();
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string>('default');
  const [page, setPage] = useState(0);
  const [attrSel, setAttrSel] = useState<Record<string, string[]>>({});

  const { data: categories } = useSWR<Category[]>('/api/public/eshop/categories', swrFetcher);
  const { data: facets } = useSWR<Record<string, string[]>>('/api/public/eshop/facets', swrFetcher);
  const { data: settings } = useSWR<{ features?: { cart?: boolean }; homepage?: { sections?: HomepageSection[] } }>(
    '/api/public/eshop/settings', swrFetcher);
  const cartEnabled = settings?.features?.cart === true;
  const { addItem } = useEshopCart(cartEnabled);
  const { showToast } = useToast();

  const toggleAttr = (key: string, value: string) => {
    setAttrSel((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      const out = { ...prev, [key]: next };
      if (next.length === 0) delete out[key];
      return out;
    });
    setPage(0);
  };

  const params = new URLSearchParams();
  if (categoryId != null) params.set('categoryId', String(categoryId));
  if (search) params.set('q', search);
  Object.entries(attrSel).forEach(([k, vs]) => vs.forEach((v) => params.append('attr', `${k}:${v}`)));
  params.set('sort', sort);
  params.set('page', String(page));
  params.set('size', '24');

  const { data, error, isLoading } = useSWR<Page<Product>>(
    `/api/public/eshop/products?${params.toString()}`,
    swrFetcher
  );

  const fmt = (n: number, currency: string) =>
    `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} ${currency || 'CZK'}`;

  const isDefaultHome = !search && categoryId == null && page === 0 && Object.keys(attrSel).length === 0;
  const sections = isDefaultHome ? (settings?.homepage?.sections || []) : [];
  const hasFeaturedSection = sections.some((s) => s.type === 'featured');
  const { data: featured } = useSWR<Product[]>(
    isDefaultHome ? '/api/public/eshop/products/featured' : null,
    swrFetcher
  );
  const showFeatured = !isLoading && featured && featured.length > 0 && !hasFeaturedSection;

  if (error) return <Alert type="error">{t('eshopLoadError')}</Alert>;

  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Category sidebar */}
      <aside className="md:w-56 shrink-0">
        <h2 className="text-sm font-semibold text-bark uppercase tracking-wide mb-2">{t('eshopCategories')}</h2>
        <ul className="space-y-1">
          <li>
            <button
              onClick={() => { setCategoryId(null); setPage(0); }}
              className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${categoryId == null ? 'bg-moss/15 text-bark font-medium' : 'text-wood hover:bg-linen'}`}
            >
              {t('eshopAllProducts')}
            </button>
          </li>
          {(categories || []).map((c) => (
            <li key={c.id}>
              <button
                onClick={() => { setCategoryId(c.id); setPage(0); }}
                className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${categoryId === c.id ? 'bg-moss/15 text-bark font-medium' : 'text-wood hover:bg-linen'}`}
              >
                {c.name}
                {c.productCount > 0 && <span className="ml-1.5 text-xs text-wood/60">({c.productCount})</span>}
              </button>
            </li>
          ))}
        </ul>

        {/* Attribute facets */}
        {facets && Object.keys(facets).length > 0 && (
          <div className="mt-6 space-y-4">
            {Object.entries(facets).map(([key, values]) => (
              <div key={key}>
                <h3 className="text-xs font-semibold text-wood uppercase tracking-wide mb-1.5">{key}</h3>
                <div className="space-y-1">
                  {values.map((v) => {
                    const checked = (attrSel[key] || []).includes(v);
                    return (
                      <label key={v} className="flex items-center gap-2 text-sm text-wood hover:text-bark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttr(key, v)}
                          className="accent-moss"
                        />
                        {v}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {Object.keys(attrSel).length > 0 && (
              <button
                onClick={() => { setAttrSel({}); setPage(0); }}
                className="text-xs text-moss hover:underline"
              >
                {t('eshopClearFilters')}
              </button>
            )}
          </div>
        )}
      </aside>

      {/* Product grid */}
      <div className="flex-1 min-w-0">
        {sections.map((s, i) => (
          <SectionBlock
            key={i}
            section={s}
            t={t}
            fmt={fmt}
            categories={categories || []}
            featured={featured || []}
            onCategorySelect={(id) => { setCategoryId(id); setPage(0); }}
          />
        ))}
        <div className="flex flex-wrap gap-3 mb-5 items-center">
          <form
            onSubmit={(e) => { e.preventDefault(); setSearch(q); setPage(0); }}
            className="flex-1 min-w-[200px]"
          >
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('eshopSearchPlaceholder')}
              className="w-full px-3 py-2 text-sm border border-wheat rounded-rustic bg-linen text-ink placeholder:text-wood/50 focus:outline-none focus:ring-2 focus:ring-moss/40"
            />
          </form>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border border-wheat rounded-rustic bg-linen text-ink"
          >
            {SORTS.map((s) => (
              <option key={s} value={s}>{t(`eshopSort_${s}`)}</option>
            ))}
          </select>
        </div>

        {isLoading && <LoadingFallback />}

        {showFeatured && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-bark uppercase tracking-wide mb-3">{t('eshopFeatured')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featured.slice(0, 4).map((p) => (
                <ProductCardLink key={p.id} product={p} fmt={fmt} t={t} />
              ))}
            </div>
          </div>
        )}

        {!isLoading && data && data.content.length === 0 && (
          <div className="text-center py-16 text-wood">{t('eshopNoProducts')}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(data?.content || []).map((p) => (
            <div
              key={p.id}
              className="group bg-linen border border-wheat rounded-rustic overflow-hidden hover:shadow-md transition-shadow flex flex-col"
            >
              <Link href={`/shop/${p.slug}`} className="flex flex-col flex-1">
                <div className="aspect-[4/3] bg-cream flex items-center justify-center overflow-hidden">
                  {p.imageAssetId ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={`/api/public/eshop/assets/${p.imageAssetId}`}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <span className="text-4xl text-wheat">🛍️</span>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <h3 className="text-sm font-medium text-bark group-hover:text-wood transition-colors line-clamp-2">
                    {p.name}
                  </h3>
                  {p.shortDescription && (
                    <p className="text-xs text-wood mt-1 line-clamp-2 flex-1">{p.shortDescription}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <PriceTag product={p} fmt={fmt} t={t} />
                    {p.stockQuantity != null && (
                      <span className={`text-xs ${p.stockQuantity > 0 ? 'text-moss' : 'text-rust'}`}>
                        {p.stockQuantity > 0 ? t('eshopInStock') : t('eshopOutOfStock')}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
              {cartEnabled && (
                <button
                  onClick={async () => {
                    try {
                      await addItem(p.id, 1);
                      showToast(t('eshopAddedToCart'), 'success');
                    } catch {
                      showToast(t('eshopCartError'), 'error');
                    }
                  }}
                  className="m-3 mt-0 btn-primary text-sm py-1.5"
                >
                  {t('eshopAddToCart')}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary disabled:opacity-40"
            >
              ← {t('previous')}
            </button>
            <span className="text-sm text-wood">{page + 1} / {data.totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages - 1, p + 1))}
              disabled={page >= data.totalPages - 1}
              className="btn-secondary disabled:opacity-40"
            >
              {t('next')} →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact product card used in featured grids. */
function ProductCardLink({ product, fmt, t }: {
  product: Product;
  fmt: (n: number, c: string) => string;
  t: (k: string) => string;
}) {
  return (
    <Link
      href={`/shop/${product.slug}`}
      className="group bg-linen border border-wheat rounded-rustic overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="aspect-[4/3] bg-cream flex items-center justify-center overflow-hidden">
        {product.imageAssetId ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`/api/public/eshop/assets/${product.imageAssetId}`}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <span className="text-3xl text-wheat">🛍️</span>
        )}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-medium text-bark line-clamp-1">{product.name}</div>
        <PriceTag product={product} fmt={fmt} t={t} small />
      </div>
    </Link>
  );
}

/** Homepage content section — text / banner / hero / category tiles / featured products. */
function SectionBlock({ section, t, fmt, categories, featured, onCategorySelect }: {
  section: HomepageSection;
  t: (k: string) => string;
  fmt: (n: number, c: string) => string;
  categories: Category[];
  featured: Product[];
  onCategorySelect: (id: number) => void;
}) {
  if (section.type === 'hero') {
    return (
      <div className="mb-6 rounded-rustic overflow-hidden border border-wheat bg-bark text-cream relative">
        {section.imageAssetId != null && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`/api/public/eshop/assets/${section.imageAssetId}`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        )}
        <div className="relative p-8 sm:p-12">
          {section.title && <h2 className="text-2xl sm:text-3xl font-semibold mb-2">{section.title}</h2>}
          {section.body && <p className="text-sm sm:text-base opacity-90 whitespace-pre-wrap max-w-xl">{section.body}</p>}
          {section.linkUrl && (
            <Link href={section.linkUrl} className="btn-secondary inline-block mt-4 text-sm">
              {section.linkLabel || t('eshopContinueShopping')}
            </Link>
          )}
        </div>
      </div>
    );
  }
  if (section.type === 'categories') {
    if (categories.length === 0) return null;
    return (
      <div className="mb-6">
        {section.title && <h2 className="text-sm font-semibold text-bark uppercase tracking-wide mb-3">{section.title}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => onCategorySelect(c.id)}
              className="bg-linen border border-wheat rounded-rustic p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="text-sm font-medium text-bark">{c.name}</div>
              {c.productCount > 0 && <div className="text-xs text-wood mt-0.5">{c.productCount}</div>}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (section.type === 'featured') {
    if (featured.length === 0) return null;
    return (
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-bark uppercase tracking-wide mb-3">
          {section.title || t('eshopFeatured')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {featured.slice(0, 4).map((p) => (
            <ProductCardLink key={p.id} product={p} fmt={fmt} t={t} />
          ))}
        </div>
      </div>
    );
  }
  if (section.type === 'banner') {
    return (
      <div className="mb-6 rounded-rustic overflow-hidden border border-wheat bg-linen flex flex-col sm:flex-row">
        {section.imageAssetId != null && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`/api/public/eshop/assets/${section.imageAssetId}`}
            alt={section.title || ''}
            className="sm:w-2/5 max-h-48 sm:max-h-none object-cover"
          />
        )}
        <div className="p-5 flex flex-col justify-center">
          {section.title && <h2 className="text-lg font-semibold text-bark mb-1">{section.title}</h2>}
          {section.body && <p className="text-sm text-wood whitespace-pre-wrap">{section.body}</p>}
          {section.linkUrl && (
            <Link href={section.linkUrl} className="text-moss hover:underline text-sm mt-2 inline-block">
              {section.linkLabel || t('eshopContinueShopping')} →
            </Link>
          )}
        </div>
      </div>
    );
  }
  // default: text block
  return (
    <div className="mb-6 p-5 rounded-rustic border border-wheat bg-linen">
      {section.title && <h2 className="text-lg font-semibold text-bark mb-1">{section.title}</h2>}
      {section.body && <p className="text-sm text-wood whitespace-pre-wrap">{section.body}</p>}
      {section.linkUrl && (
        <Link href={section.linkUrl} className="text-moss hover:underline text-sm mt-2 inline-block">
          {section.linkLabel || t('eshopContinueShopping')} →
        </Link>
      )}
    </div>
  );
}

/** Price display — group price (B2B) with struck-through base, or "login for price" when null. */
function PriceTag({
  product,
  fmt,
  t,
  small,
}: {
  product: Product;
  fmt: (n: number, c: string) => string;
  t: (k: string) => string;
  small?: boolean;
}) {
  if (product.price == null && product.groupPrice == null) {
    return <span className={`${small ? 'text-xs' : 'text-sm'} text-wood`}>{t('eshopPriceOnLogin')}</span>;
  }
  if (product.groupPrice != null && product.price != null && product.groupPrice < product.price) {
    return (
      <span>
        <span className={`${small ? 'text-sm' : 'text-base'} font-semibold text-moss`}>
          {fmt(product.groupPrice, product.currency)}
        </span>
        <span className={`${small ? 'text-[10px]' : 'text-xs'} text-wood line-through ml-1.5`}>
          {fmt(product.price, product.currency)}
        </span>
      </span>
    );
  }
  return (
    <span className={`${small ? 'text-sm' : 'text-base'} font-semibold text-bark`}>
      {fmt(product.groupPrice ?? product.price ?? 0, product.currency)}
    </span>
  );
}
